<template>
  <div class="px-4 sm:px-6 py-8 sm:py-14 max-w-3xl mx-auto">
    <div class="relative overflow-hidden bg-white dark:bg-[#1C2128] rounded-2xl sm:rounded-xl border border-gray-100 dark:border-gray-700">
      <!-- Soft glow behind the icon -->
      <div class="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[28rem] h-[28rem] rounded-full bg-gradient-to-b from-blue-100/70 via-indigo-50/40 to-transparent dark:from-blue-500/10 dark:via-indigo-500/5 blur-2xl"></div>

      <div class="relative flex flex-col items-center text-center px-6 pt-12 pb-8 sm:px-12">
        <div class="relative mb-6">
          <div class="absolute inset-0 rounded-3xl bg-blue-500/20 dark:bg-blue-400/10 blur-xl"></div>
          <div class="relative w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25 flex items-center justify-center">
            <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path v-for="(d, i) in content.icon" :key="i" stroke-linecap="round" stroke-linejoin="round" :d="d"/>
            </svg>
          </div>
        </div>

        <span class="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
          <span class="relative flex w-1.5 h-1.5">
            <span class="absolute inline-flex w-full h-full rounded-full bg-blue-400 opacity-75 animate-ping"></span>
            <span class="relative inline-flex w-1.5 h-1.5 rounded-full bg-blue-500"></span>
          </span>
          Coming soon
        </span>

        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{{ content.title }}</h2>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md leading-relaxed">{{ content.description }}</p>
      </div>

      <!-- What to expect -->
      <div class="relative border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/20 px-6 py-6 sm:px-10">
        <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4 text-center">What to expect</p>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div v-for="item in content.highlights" :key="item.title"
            class="flex sm:flex-col items-start gap-3 sm:gap-2 p-4 rounded-xl bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700">
            <div class="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
              <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
              </svg>
            </div>
            <div class="min-w-0 text-left">
              <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ item.title }}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{{ item.text }}</p>
            </div>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row items-center justify-center gap-2.5 mt-6">
          <router-link :to="`/events/${event.id}/bookings`" data-turbo-prefetch="false"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
            View bookings
          </router-link>
          <button v-if="publicUrl" type="button" @click="copyPublicUrl"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            {{ copyLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useConstants } from 'composables/useConstants.js'
import { EVENT_ICONS } from 'controllers/vue_components/events/eventIcons.js'

const props = defineProps({
  event: { type: Object, required: true },
  feature: { type: String, required: true },
})

const { APPLICATION_URL } = useConstants()

const content = computed(() => {
  const name = props.event.name
  const byFeature = {
    analytics: {
      title: 'Event analytics',
      description: `See how ${name} performs over time. Booking trends, cancellations and your most loyal attendees, all in one place.`,
      highlights: [
        { title: 'Booking trends', text: 'Daily, weekly and monthly booking volume.' },
        { title: 'Cancellation rate', text: 'Spot drops before they add up.' },
        { title: 'Top attendees', text: 'Know who books you the most.' },
      ],
    },
    qr_code: {
      title: 'QR code',
      description: `Print or share a QR code that opens the ${name} booking page in a single scan.`,
      highlights: [
        { title: 'Download anywhere', text: 'High resolution PNG and SVG files.' },
        { title: 'Your brand', text: 'Match the code to your colors.' },
        { title: 'Always current', text: 'Keeps working when you edit the event.' },
      ],
    },
    embed: {
      title: 'Embed code',
      description: `Put the ${name} booking page right on your website so visitors can schedule without leaving it.`,
      highlights: [
        { title: 'Inline widget', text: 'Show the booking page inside any page.' },
        { title: 'Popup widget', text: 'Open booking in an overlay on click.' },
        { title: 'Copy and paste', text: 'One snippet. No developer needed.' },
      ],
    },
    link_button: {
      title: 'Link button',
      description: `Add a Book now button for ${name} to your site, emails or social profiles.`,
      highlights: [
        { title: 'Custom styling', text: 'Pick the label, colors and shape.' },
        { title: 'Live preview', text: 'See the button before you copy it.' },
        { title: 'Works everywhere', text: 'Websites, newsletters and link pages.' },
      ],
    },
  }
  return { ...(byFeature[props.feature] || byFeature.analytics), icon: EVENT_ICONS[props.feature] || EVENT_ICONS.analytics }
})

const publicUrl = computed(() =>
  props.event.absolute_url ? `${APPLICATION_URL}${props.event.absolute_url}` : ''
)

const copyState = ref('idle')
const copyLabel = computed(() => ({
  idle: 'Copy booking link',
  copied: 'Copied',
  failed: 'Could not copy',
}[copyState.value]))

async function copyPublicUrl() {
  try {
    await navigator.clipboard.writeText(publicUrl.value)
    copyState.value = 'copied'
  } catch {
    copyState.value = 'failed'
  }
  setTimeout(() => { copyState.value = 'idle' }, 2000)
}
</script>
